import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { useCurrentUser, isPaidPlan } from "@/hooks/useCurrentUser";
import { useBillingCheckout, useBillingPortal, useBillingConfig } from "@/hooks/useBilling";

export interface BillingSearch {
  checkout?: "monthly" | "annual";
  promo?: string;
  status?: "success" | "cancelled";
}

export function BillingPanel({ checkout, promo, status }: BillingSearch) {
  const { data: user, refetch } = useCurrentUser();
  const checkoutMut = useBillingCheckout();
  const portalMut = useBillingPortal();
  const [period, setPeriod] = useState<"monthly" | "annual">("annual");
  const [activationTimedOut, setActivationTimedOut] = useState(false);
  const isPaid = isPaidPlan(user?.plan);
  // Default disabled until the probe resolves, so an upgrade CTA never flashes
  // when self-serve billing isn't actually live (e.g. during the beta).
  const { data: billingConfig } = useBillingConfig();
  const billingEnabled = billingConfig?.enabled === true;

  // Resume-from-landing: auto-start checkout once for a free user with intent.
  // The `autofired` ref (not the deps) enforces once-only; `checkoutMut` is left
  // out of deps because useMutation returns a fresh object each render, which
  // would re-register the effect needlessly.
  const autofired = useRef(false);
  useEffect(() => {
    if (autofired.current || !checkout || !user || isPaid || !billingEnabled) return;
    autofired.current = true;
    checkoutMut.mutate({ period: checkout, promo });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkout, promo, user, isPaid, billingEnabled]);

  // Webhook can lag the success redirect — poll /me until plan flips (~10s).
  // If it hasn't flipped by then the user has still PAID, so surface a manual
  // refresh path rather than leaving them stuck on "Activating…".
  useEffect(() => {
    if (status !== "success" || isPaid) return;
    const tick = setInterval(() => refetch(), 1500);
    const stop = setTimeout(() => {
      clearInterval(tick);
      setActivationTimedOut(true);
    }, 10_000);
    return () => {
      clearInterval(tick);
      clearTimeout(stop);
    };
  }, [status, isPaid, refetch]);

  // Beta: self-serve billing isn't live yet. Show a calm note instead of an
  // upgrade button (which would 503) or a portal link (also 503). Covers both
  // free and admin-granted-Pro testers; un-hides automatically at cutover.
  if (!billingEnabled) {
    return (
      <p className="text-sm text-muted-foreground">
        Billing isn't open during the beta — you've been granted full Pro
        access. If anything looks locked, reach out and we'll sort it.
      </p>
    );
  }

  if (checkoutMut.isError) {
    return (
      <p className="text-sm text-muted-foreground">
        Billing isn't available yet. Please check back soon.
      </p>
    );
  }

  if (status === "success" && !isPaid) {
    if (activationTimedOut) {
      return (
        <div className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Your payment went through, but activating Pro is taking longer than expected.
            Refresh in a moment — if it persists, contact support.
          </p>
          <Button onClick={() => refetch()}>Refresh</Button>
        </div>
      );
    }
    return <p className="text-sm text-muted-foreground">Activating Pro…</p>;
  }

  if (isPaid) {
    return (
      <div className="space-y-4">
        <p className="text-sm">You're on <span className="font-medium">Pro</span>.</p>
        <Button onClick={() => portalMut.mutate()} disabled={portalMut.isPending}>
          Manage subscription
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {status === "cancelled" && (
        <p className="text-sm text-muted-foreground">Checkout cancelled — you're still on the free plan.</p>
      )}
      <p className="text-sm text-muted-foreground">
        {user?.concept_count ?? 0} / {user?.plan_limits?.max_concepts ?? 1} concepts used on the free plan.
      </p>
      <div className="inline-flex rounded-md border p-0.5 text-sm">
        <button
          className={`rounded px-3 py-1 ${period === "annual" ? "bg-muted font-medium" : "text-muted-foreground"}`}
          onClick={() => setPeriod("annual")}
        >
          Annual <span className="text-xs text-muted-foreground">($19.20/mo)</span>
        </button>
        <button
          className={`rounded px-3 py-1 ${period === "monthly" ? "bg-muted font-medium" : "text-muted-foreground"}`}
          onClick={() => setPeriod("monthly")}
        >
          Monthly <span className="text-xs text-muted-foreground">($24/mo)</span>
        </button>
      </div>
      <div>
        <Button onClick={() => checkoutMut.mutate({ period })} disabled={checkoutMut.isPending}>
          Upgrade to Pro
        </Button>
      </div>
    </div>
  );
}
